import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import useIsMobile from "shared/core/hooks/useIsMobile";
import { Breakpoint } from "shared/UI/ResponsiveBreakpoints";
import { HorizontalScroll } from "shared/UI/HorizontalScroll/HorizontalScroll";
import { DesktopCarousel } from "UIPalette/DesktopCarousel/DesktopCarousel";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import Button from "shared/UI/Buttons/Button";
import { Container } from "shared/UI/LayoutComponents/LayoutComponents";

interface HubResourcesModuleProps {
  index: number;
  hasPreviousVisibleModule?: boolean;
  heading: string;
  cards?: ReactNode[];
  isLoading: boolean;
  error: unknown;
  mutate: () => void;
  onSeeAllClick: () => void;
  className?: string;
}
export const HubResourcesModule = ({
  index,
  hasPreviousVisibleModule = true,
  heading,
  cards,
  isLoading,
  error,
  mutate,
  onSeeAllClick,
  className,
}: HubResourcesModuleProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "studentHub.resources" });
  const isMobile = useIsMobile(Breakpoint.Desktop);

  const hasCards = !!cards?.length;
  const showTopBorder = index !== 0 && hasPreviousVisibleModule;

  const header = (
    <Container className={classNames("!h-auto !my-0", { "!pt-12 border-t-none lg:border-t border-brand-grey-light": showTopBorder })}>
      <div className="flex w-full justify-between items-center">
        <h2 className="!p-0">{heading}</h2>
        <Button className="max-w-[100px] !w-auto !h-auto" variant="text" text={t("seeAllCta")} onClick={onSeeAllClick} />
      </div>
    </Container>
  );

  const mobileLayout = (
    <HorizontalScroll className="hide-scrollbar">
      <Container className="!mt-4">
        <div className="flex gap-6">
          {cards}
          <div className="min-w-[1px] min-h-full"></div>
        </div>
      </Container>
    </HorizontalScroll>
  );

  const desktopLayout = (
    <Container className="!mt-0">
      <DesktopCarousel>{cards}</DesktopCarousel>
    </Container>
  );

  const cardsLayout = isMobile ? mobileLayout : desktopLayout;

  if (!hasCards) return null;

  return (
    <div
      data-testid="hub-resources-module"
      className={classNames("h-[336px] lg:mt-4", { "border-t lg:border-none border-brand-grey-light": showTopBorder }, className)}
    >
      <div
        className={classNames(
          isLoading || !!error ? "flex flex-col items-center h-full w-full justify-center" : "left-0 lg:-left-1.5 absolute w-screen"
        )}
      >
        <ViewStateWrapper errorMessage={t("errorMessage")} onRetry={mutate} loading={isLoading} error={!!error}>
          {header}
          {cardsLayout}
        </ViewStateWrapper>
      </div>
    </div>
  );
};
